/**
 * iter200 — Modal de composition d'un email métier (Facture / Devis / OR).
 *
 * Flux :
 *   1. À l'ouverture, appelle `GET /api/mail/business/prepare?kind=X&id=Y`
 *      pour récupérer : destinataire (email client), sujet, corps HTML
 *      pré-remplis (template statique + contexte).
 *   2. L'admin peut modifier tous les champs :
 *        - À (obligatoire)
 *        - CC (comma-separated)
 *        - Sujet
 *        - Corps (Textarea multi-lignes, la conversion \n → <br/> se fait à l'envoi)
 *        - Pièces jointes supplémentaires (le PDF de l'entité est joint automatiquement)
 *   3. Envoie via `POST /api/mail/business/send`. Le message envoyé apparaît
 *      immédiatement dans la Boîte mail (thread client).
 *
 * Props :
 *   - open (bool), onClose (fn)
 *   - kind ('facture'|'devis'|'or'), entityId (str), entityLabel (str, affiché)
 *   - onSent (fn(result)) — callback après envoi réussi
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Send, X, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const MAX_ATTACHMENT_MB = 10;

export default function EmailComposeModal({ open, onClose, kind, entityId, entityLabel, onSent }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [toOptions, setToOptions] = useState([]);   // iter237
  const [toSource, setToSource] = useState("");     // iter345
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [pdfAttached, setPdfAttached] = useState(true);
  const [orAttachments, setOrAttachments] = useState([]);       // iter230
  const [selectedOrAtts, setSelectedOrAtts] = useState([]);     // iter230

  useEffect(() => {
    if (!open || !kind || !entityId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/mail/business/prepare?kind=${kind}&id=${entityId}`);
        if (cancelled) return;
        setTo(data.to || "");
        setToOptions(data.to_options || []);
        setToSource(data.to_source || "");
        setCc("");
        setSubject(data.subject || "");
        // Convertit le HTML minimal en texte multi-lignes pour la Textarea
        const bodyPlain = String(data.body_html || "")
          .replace(/<br\s*\/?\s*>/gi, "\n")
          .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
          .replace(/<[^>]+>/g, "")
          .trim();
        setBodyText(bodyPlain);
        setAttachments([]);
        setPdfAttached(!!data.pdf_available);
        // iter230 — PJ de l'OR envoyées en fichiers séparés (plus de fusion PDF)
        const orAtts = data.or_attachments || [];
        setOrAttachments(orAtts);
        setSelectedOrAtts(orAtts.filter((a) => a.selected_default).map((a) => a.id));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossible de préparer l'email");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, kind, entityId]);

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = [];
    for (const f of files) {
      if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        toast.error(`${f.name} dépasse ${MAX_ATTACHMENT_MB} Mo`);
        continue;
      }
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1] || "");
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      added.push({ filename: f.name, mime: f.type || "application/octet-stream", content_b64: b64, size: f.size });
    }
    setAttachments((prev) => [...prev, ...added]);
    e.target.value = "";
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const send = async () => {
    if (!to.trim() || !to.includes("@")) {
      toast.error("Adresse « À » manquante ou invalide");
      return;
    }
    if (!subject.trim()) {
      toast.error("Objet manquant");
      return;
    }
    setSending(true);
    try {
      const cc_list = cc.split(",").map((s) => s.trim()).filter((s) => s && s.includes("@"));
      const body_html = bodyText
        .split(/\n\s*\n/)
        .map((para) => `<p>${para.replace(/\n/g, "<br/>")}</p>`)
        .join("\n");
      const { data } = await api.post("/mail/business/send", {
        kind, entity_id: entityId, to: to.trim(), cc: cc_list,
        subject: subject.trim(), body_html,
        attachments,
        include_entity_pdf: pdfAttached,
        or_attachment_ids: selectedOrAtts,
      });
      toast.success(`Email envoyé à ${data.to}${data.cc?.length ? ` (CC: ${data.cc.length})` : ""}`);
      onSent?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="email-compose-modal">
        <DialogHeader>
          <DialogTitle>
            Envoyer par email {entityLabel ? `— ${entityLabel}` : ""}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Préparation…
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>À *</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@exemple.ch" data-testid="email-compose-to" />
              {toOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="email-compose-to-options">
                  {toOptions.map((opt, i) => {
                    const active = opt.email.toLowerCase() === to.trim().toLowerCase();
                    return (
                      <button
                        key={opt.email}
                        type="button"
                        onClick={() => setTo(opt.email)}
                        className={`px-2 py-1 rounded-full border text-xs transition-colors ${
                          active
                            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                        data-testid={`email-compose-to-option-${i}`}
                        title={opt.label}
                      >
                        {opt.email}
                        <span className="ml-1 text-[10px] opacity-70">{opt.label.split("—").pop().trim()}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {/* iter345 — provenance de l'adresse pré-remplie */}
              {toSource && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="email-compose-to-source">
                  Adresse pré-remplie : {toSource}
                  {toOptions.length === 1
                    && " — c'est la seule adresse enregistrée sur la fiche client"}
                </p>
              )}
            </div>
            <div>
              <Label>CC (copie visible)</Label>
              <Input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="collaborateur@exemple.ch, autre@exemple.ch"
                data-testid="email-compose-cc"
              />
              <p className="text-xs text-muted-foreground mt-1">Adresses séparées par des virgules. Aucun BCC automatique.</p>
            </div>
            <div>
              <Label>Objet *</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-compose-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={8} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="email-compose-body" />
            </div>
            {(kind === "facture" || kind === "devis" || kind === "or") && (
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="email-compose-pdf-toggle-wrap">
                <input
                  type="checkbox"
                  checked={pdfAttached}
                  onChange={(e) => setPdfAttached(e.target.checked)}
                  data-testid="email-compose-pdf-toggle"
                />
                <FileText className="w-4 h-4" />
                Joindre le PDF {kind === "facture" ? "de la facture" : "du devis"} automatiquement
              </label>
            )}
            {orAttachments.length > 0 && (
              <div className="border border-gray-200 rounded p-2.5" data-testid="email-compose-or-attachments">
                <Label className="text-xs">Pièces jointes de l&apos;OR (fichiers séparés)</Label>
                <p className="text-[11px] text-muted-foreground mb-1.5">
                  Chaque pièce cochée part comme fichier distinct, avec son nom. Renommez-les depuis la fiche OR.
                </p>
                {orAttachments.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedOrAtts.includes(a.id)}
                      onChange={(e) => setSelectedOrAtts((prev) => (
                        e.target.checked ? [...prev, a.id] : prev.filter((x) => x !== a.id)
                      ))}
                      data-testid={`email-compose-or-att-${a.id}`}
                    />
                    <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                    <span className="flex-1 truncate">{a.filename}</span>
                    <span className="text-xs text-muted-foreground">{Math.round((a.size_bytes || 0) / 1024)} Ko</span>
                  </label>
                ))}
              </div>
            )}
            <div>
              <Label>Pièces jointes supplémentaires</Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  onChange={onPickFiles}
                  className="text-xs"
                  data-testid="email-compose-file-input"
                />
                <span className="text-xs text-muted-foreground">Max {MAX_ATTACHMENT_MB} Mo par fichier</span>
              </div>
              {attachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {attachments.map((a, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm bg-muted rounded px-2 py-1" data-testid={`email-compose-attachment-${i}`}>
                      <Paperclip className="w-3.5 h-3.5" />
                      <span className="flex-1 truncate">{a.filename}</span>
                      <span className="text-xs text-muted-foreground">{Math.round(a.size / 1024)} Ko</span>
                      <button type="button" onClick={() => removeAttachment(i)} className="text-red-600 hover:text-red-800" data-testid={`email-compose-attachment-remove-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="email-compose-cancel">
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
          <Button onClick={send} disabled={loading || sending} data-testid="email-compose-send">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
